import {
  OPENPANEL_CLIENT_ID,
  OPENPANEL_SELF_API_URL,
  OPENPANEL_SELF_CLIENT_ID,
  isOpenPanelCloudEnabled,
  isOpenPanelAllowedHost,
  isOpenPanelProdBuild,
} from '../config/openpanel';
import { getPlausibleAttributionProps } from './plausible';
import { getUtmParams } from './utm';

/** Primary client — self-hosted (op.daiv.live) when configured; cloud-only fallback if self missing. */
export let op = null;

/** Legacy cloud dual-write client (api.openpanel.dev). Same as `op` when cloud-only. */
export let opCloud = null;

/** @deprecated Use `op` (self-hosted is always primary). */
export let opSelf = null;

let openPanelSdkPromise = null;

function loadOpenPanelSdk() {
  if (!openPanelSdkPromise) {
    openPanelSdkPromise = import(
      /* webpackChunkName: "openpanel-sdk" */ '@openpanel/web'
    ).then((mod) => mod.OpenPanel);
  }
  return openPanelSdkPromise;
}

function createOpenPanelClient(OpenPanel, options) {
  return new OpenPanel({
    trackScreenViews: true,
    trackOutgoingLinks: true,
    trackAttributes: true,
    ...options,
  });
}

/** Active clients — self-hosted first, then optional cloud dual-write. */
export function getOpenPanelClients() {
  const clients = [];
  if (op && typeof op.track === 'function') clients.push(op);
  if (opCloud && opCloud !== op && typeof opCloud.track === 'function') clients.push(opCloud);
  return clients;
}

function dispatchOpenPanelReady() {
  if (window.__daivOpenPanelReadyDispatched) return;
  window.__daivOpenPanelReadyDispatched = true;
  window.dispatchEvent(new CustomEvent('daiv-openpanel-ready'));
}

/** UTM (+ related) keys OpenPanel Sources/Medium/Campaign read from `__query`. */
const OPENPANEL_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
];

/**
 * OpenPanel session UTM dimensions come from reserved `__query` (and `__path` search),
 * not from flat custom props like `utm_source` on HomepageLanded.
 * @see https://gist.github.com/lindesvard/1f2e8e22198e1ff98e7300be26880865
 */
export function getOpenPanelQuery() {
  if (typeof window === 'undefined') return {};
  const attribution = getPlausibleAttributionProps();
  const params = new URLSearchParams(window.location.search || '');
  const stored = getUtmParams();
  const query = {};
  for (const key of OPENPANEL_QUERY_KEYS) {
    const v = String(
      attribution[key] || params.get(key) || stored[key] || '',
    ).trim();
    if (v) query[key] = v;
  }
  return query;
}

/** Props for every OpenPanel event: flat UTMs (Events filters) + `__query` (Overview Sources). */
export function getOpenPanelAttributionPayload() {
  const attribution = getPlausibleAttributionProps();
  const query = getOpenPanelQuery();
  return {
    ...attribution,
    ...(Object.keys(query).length ? { __query: query } : {}),
  };
}

/**
 * Full URL for screen_view `__path` (include utm_* search so server can parse too).
 */
export function getOpenPanelScreenUrl(pathname) {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const path = pathname != null ? String(pathname) : window.location.pathname;
  const hash = window.location.hash || '';

  const params = new URLSearchParams(window.location.search || '');
  const query = getOpenPanelQuery();
  for (const [key, value] of Object.entries(query)) {
    if (!params.get(key)) params.set(key, value);
  }
  const q = params.toString();
  return `${origin}${path || '/'}${q ? `?${q}` : ''}${hash}`;
}

/** Fire screen_view with UTM on `__path` + `__query` so Overview Sources fills correctly. */
export function trackOpenPanelScreenView(pathname) {
  const clients = getOpenPanelClients();
  if (!clients.length) return;
  const payload = getOpenPanelAttributionPayload();
  const url = getOpenPanelScreenUrl(pathname);
  clients.forEach((client) => {
    if (Object.keys(payload).length && typeof client.setGlobalProperties === 'function') {
      client.setGlobalProperties(payload);
    }
    if (typeof client.screenView === 'function') {
      client.screenView(url, payload);
    }
  });
}

/** Load OpenPanel on prod site hosts (see src/config/openpanel.js). */
export function ensureOpenPanelInit() {
  if (typeof window === 'undefined') return Promise.resolve();

  if (!isOpenPanelProdBuild() || !isOpenPanelAllowedHost(window.location.hostname)) {
    window.__daivOpenPanelAllowed = false;
    return Promise.resolve();
  }

  window.__daivOpenPanelAllowed = true;
  if (window.__daivOpenPanelInitialized) {
    dispatchOpenPanelReady();
    return Promise.resolve();
  }

  return loadOpenPanelSdk()
    .then((OpenPanel) => {
      if (window.__daivOpenPanelInitialized) {
        dispatchOpenPanelReady();
        return;
      }

      op = null;
      opCloud = null;
      opSelf = null;

      const selfClientId = String(OPENPANEL_SELF_CLIENT_ID || '').trim();
      const selfApiUrl = String(OPENPANEL_SELF_API_URL || '').trim();
      if (selfClientId && selfApiUrl) {
        try {
          op = createOpenPanelClient(OpenPanel, {
            clientId: selfClientId,
            apiUrl: selfApiUrl,
          });
          opSelf = op;
        } catch {
          op = null;
          opSelf = null;
        }
      }

      if (isOpenPanelCloudEnabled()) {
        try {
          opCloud = createOpenPanelClient(OpenPanel, {
            clientId: OPENPANEL_CLIENT_ID,
          });
        } catch {
          opCloud = null;
        }
      }

      if (!op && opCloud) {
        op = opCloud;
        opCloud = null;
        opSelf = null;
      }

      if (!op) {
        return;
      }

      const payload = getOpenPanelAttributionPayload();
      if (Object.keys(payload).length) {
        getOpenPanelClients().forEach((client) => {
          if (typeof client.setGlobalProperties === 'function') {
            client.setGlobalProperties(payload);
          }
        });
      }

      trackOpenPanelScreenView(window.location.pathname);

      window.op = op;
      if (opCloud) window.opCloud = opCloud;
      else delete window.opCloud;
      if (opSelf && opSelf === op) window.opSelf = op;
      else delete window.opSelf;

      window.__daivOpenPanelInitialized = true;
      dispatchOpenPanelReady();
    })
    .catch(() => {});
}
